import { useEffect, useState } from "react";
import { ApiResponse } from "../api/apiResponse";
import {
  indexTutorAvailable,
  indexTutorSchedulesSlot,
} from "../api/endpoints/tutors/scheduleSlots";
import { makeApiRequest } from "../helpers";
import { getDuration } from "../helpers/date_helpers";
import ApiDateParser from "../helpers/parsers/apiDateParser";
import { getHoursList } from "../services/schdeuleTimeOptionsService";
import { useAuth } from "../context/AuthContext";
import { indexTutorStudentSchedules } from "../api/endpoints/students/schedules";

type Hash = { [key: string]: any };
type Schedule = {
  [key: string]: Hash[];
};

/**
 *  Divide time between start and and time of the slot into hours and return each hour as separate slot
 */
const generateHoursSlots = (slot: Hash) => {
  const startTime = ApiDateParser.parse(slot.start_time);
  const endTime = ApiDateParser.parse(slot.end_time);
  const dayHoursList = getHoursList(
    startTime,
    getDuration(startTime, endTime).asHours()
  );
  return dayHoursList.map((hour) => ({
    id: `${slot.id}${hour.format()}`,
    start_time: ApiDateParser.toApiDate(hour),
    end_time: ApiDateParser.toApiDate(hour.add(1, "hours")),
    schedule_slot: slot,
  }));
};

const slotReducer = (scheduleMap: Schedule, slot: Hash) => {
  if (!scheduleMap[slot.day_of_the_week])
    scheduleMap[slot.day_of_the_week] = [];

  const parsedSlots = generateHoursSlots(slot);
  scheduleMap[slot.day_of_the_week].push(...parsedSlots);
  return scheduleMap;
};

/**
 *
 * Returns tutor availability schedule in the same form of the recitation schedule that is returned from the API.
 * It can be passed easily to the CalendarScheduleModel
 * @example {
 *    "thursday": [
 *          {
 *              "id": 445,
 *              "start_time": "2000-01-01 07:00:00 UTC",
 *              "end_time": "2000-01-01 21:00:00 UTC",
 *              "schedule_slot": {
 *                  "id": 445,
 *                  "created_at": "2022-06-02 10:44:37 UTC",
 *                  "day_of_the_week": "thursday",
 *                  "end_time": "2000-01-01 21:00:00 UTC",
 *                  "object_type": "ScheduleSlot",
 *                  "start_time": "2000-01-01 07:00:00 UTC",
 *                  "updated_at": "2022-06-02 15:34:31 UTC"
 *              }
 *          }
 *      ]
 *  }
 */
export function useAvailabilitySchedule(): [Schedule, Function] {
  const [availabilitySchedule, setSchedule] = useState<Schedule>({});
  const { userType }: any = useAuth();

  const getScheduleSlotsMap = async () => {
    if (userType === "tutor") {
      await makeApiRequest(indexTutorSchedulesSlot, (res: ApiResponse) => {
        let scheduleMap: Schedule = {};
        const scheduleSlots = res.data;
        if (!!scheduleSlots && scheduleSlots.length > 0) {
          scheduleMap = scheduleSlots.reduce(slotReducer, {});
        }
        setSchedule(scheduleMap);
      });
    } else if (userType === "student") {
      await makeApiRequest(indexTutorStudentSchedules, (res: ApiResponse) => {
        let scheduleMap: Schedule = {};
        const scheduleSlots: any = res.data;
        if (!!scheduleSlots && Object.keys(scheduleSlots).length > 0) {
          setSchedule(scheduleSlots);
        }
      });
    }
  };

  useEffect(() => {
    getScheduleSlotsMap();
  }, []);

  return [availabilitySchedule, getScheduleSlotsMap];
}

export function useAvailabilityTutorSchedule(): [Schedule, Function] {
  const [availabilityTutorSchedule, setSchedule] = useState<Schedule>({});

  const getScheduleSlotsMap = async () => {
    await makeApiRequest(indexTutorAvailable, (res: ApiResponse) => {
      const scheduleSlots: Schedule = res.data;
      if (!!scheduleSlots && Object.keys(scheduleSlots).length > 0) {
        setSchedule(scheduleSlots);
      }
    });
  };

  useEffect(() => {
    getScheduleSlotsMap();
  }, []);

  return [availabilityTutorSchedule, getScheduleSlotsMap];
}
